import React, { useState, useEffect } from 'react';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface ContactInfo {
  _id: string;
  type: string;
  title: string;
  description: string;
  contact: string;
  icon: string;
  imageUrl?: string;
  featured: boolean;
  displayOrder: number;
}

interface FormData {
  type: string;
  title: string;
  description: string;
  contact: string;
  icon: string;
  imageUrl: string;
  featured: boolean;
  displayOrder: number;
}

const CONTACT_TYPES = [
  'Email',
  'Telegram',
  'Discord',
  'Twitter',
  'LinkedIn',
  'GitHub',
  'Website',
  'Office',
  'Other'
];

const ICON_OPTIONS = [
  { value: '📧', label: '📧 Email' },
  { value: '💬', label: '💬 Telegram' },
  { value: '🎮', label: '🎮 Discord' },
  { value: '🐦', label: '🐦 Twitter' },
  { value: '💼', label: '💼 LinkedIn' },
  { value: '🐙', label: '🐙 GitHub' },
  { value: '🌐', label: '🌐 Website' },
  { value: '🏢', label: '🏢 Office' },
  { value: '📞', label: '📞 Phone' },
  { value: '💡', label: '💡 Other' }
];

const ContactSection: React.FC = () => {
  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactInfo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    type: 'Email',
    title: '',
    description: '',
    contact: '',
    icon: '📧',
    imageUrl: '',
    featured: false,
    displayOrder: 1
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/contacts`);
      if (response.ok) {
        const result = await response.json();
        const data = result.success ? result.data : result;
        setContacts(data.sort((a: ContactInfo, b: ContactInfo) => a.displayOrder - b.displayOrder));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('logo', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/upload/logo`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData,
      });

      const result = await response.json();

      if (result.url) {
        setFormData(prev => ({
          ...prev,
          imageUrl: result.url
        }));
        
        console.log('Image uploaded to:', result.url);
        alert('Image uploaded successfully!');
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const url = editingContact ? `${apiUrl}/api/contacts/${editingContact._id}` : `${apiUrl}/api/contacts`;
      const method = editingContact ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchContacts();
        resetForm();
        alert(editingContact ? 'Contact updated!' : 'Contact added!');
      } else {
        alert('Error saving contact');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Error saving contact');
    }
  };

  const handleEdit = (contact: ContactInfo) => {
    setEditingContact(contact);
    setFormData({
      type: contact.type,
      title: contact.title,
      description: contact.description,
      contact: contact.contact,
      icon: contact.icon,
      imageUrl: contact.imageUrl || '',
      featured: contact.featured,
      displayOrder: contact.displayOrder
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchContacts();
        alert('Contact deleted!');
      } else {
        alert('Error deleting contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'Email',
      title: '',
      description: '',
      contact: '',
      icon: '📧',
      imageUrl: '',
      featured: false,
      displayOrder: Math.max(...contacts.map(c => c.displayOrder), 0) + 1
    });
    setEditingContact(null);
    setShowForm(false);
  };

  const moveContact = async (contactId: string, direction: 'up' | 'down') => {
    const contact = contacts.find(c => c._id === contactId);
    if (!contact) return;

    const currentIndex = contacts.findIndex(c => c._id === contactId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= contacts.length) return;

    const targetContact = contacts[targetIndex];
    
    try {
      // Swap display orders
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      await fetch(`${apiUrl}/api/contacts/${contact._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ...contact, displayOrder: targetContact.displayOrder }),
      });

      await fetch(`${apiUrl}/api/contacts/${targetContact._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ...targetContact, displayOrder: contact.displayOrder }),
      });

      fetchContacts();
    } catch (error) {
      console.error('Error moving contact:', error);
      alert('Error moving contact');
    }
  };

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Contact Management</h1>
            <p className="text-gray-400">Manage DUSKPROOF contact information and social links</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
          >
            {showForm ? 'Cancel' : 'Add Contact'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                >
                  {CONTACT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                >
                  {ICON_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  placeholder="e.g., General Inquiries"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Information
                </label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  placeholder="e.g., hello@duskproof.com or @duskproof"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">
                  Image (Optional)
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    className="px-3 py-1 bg-dusk-copper hover:bg-dusk-copper-deep text-white text-xs rounded"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Browse'}
                  </button>
                  {formData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="Upload an image or enter URL manually"
                  readOnly={formData.imageUrl?.startsWith('/uploads/') || false}
                />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              {formData.imageUrl && (
                <div className="mt-2 p-2 bg-[rgba(243,235,226,0.04)] rounded">
                  <p className="text-xs text-gray-400 mb-2">Image Preview:</p>
                  <img 
                    src={formData.imageUrl} 
                    alt="Contact preview" 
                    className="w-16 h-16 object-cover rounded border"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white h-20 resize-none"
                placeholder="Brief description of this contact method..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="featured" className="text-sm font-medium text-gray-300">
                  Featured Contact
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  min="1"
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-teal-500/30"
              >
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Contacts Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/30">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Featured
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {contacts.map((contact, index) => (
                  <tr key={contact._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-300">{contact.displayOrder}</span>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => moveContact(contact._id, 'up')}
                            disabled={index === 0}
                            className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveContact(contact._id, 'down')}
                            disabled={index === contacts.length - 1}
                            className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {contact.imageUrl ? (
                            <img
                              className="h-10 w-10 rounded object-cover"
                              src={getFullImageUrl(contact.imageUrl)}
                              alt={contact.title}
                              onError={handleImageError}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-gray-600 flex items-center justify-center">
                              <span className="text-lg">{contact.icon}</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{contact.title}</div>
                          <div className="text-sm text-gray-400 max-w-sm truncate">{contact.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {contact.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{contact.contact}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.featured ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Featured
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Regular
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all" title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button
                          onClick={() => handleDelete(contact._id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
